import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Workspace } from 'src/app/models/workspace.interface';
import { UserInterface } from 'src/app/models/user.interface';
import { WorkspaceService } from 'src/app/core/services/workspace.service';
import { UserService } from 'src/app/core/services/user.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'globot-workspace-selector',
  templateUrl: './workspace-selector.component.html',
  styleUrls: ['./workspace-selector.component.scss'],
  providers: [MessageService],
})
export class WorkspaceSelectorComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;

  workspaces: Workspace[] = [];
  selectedWorkspaceId: string | null = null;
  loading = true;
  user: UserInterface | undefined;

  private subscription = new Subscription();

  constructor(
    private workspaceService: WorkspaceService,
    private userService: UserService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.user = this.userService.getUserLocalStorage();

    this.subscription.add(
      this.userService.user$.subscribe((user) => {
        if (user) {
          this.user = user;
          this.sortWorkspaces();
        }
      })
    );

    this.loadWorkspaces();

    this.subscription.add(
      this.workspaceService.getCurrentWorkspaceObservable().subscribe((ws) => {
        if (ws) {
          this.selectedWorkspaceId = ws.id;
          const idx = this.workspaces.findIndex((w) => w.id === ws.id);
          if (idx !== -1) {
            this.workspaces[idx] = { ...this.workspaces[idx], ...ws };
            this.workspaces = [...this.workspaces];
          }
        }
      })
    );
  }

  private loadWorkspaces(): void {
    this.loading = true;
    this.subscription.add(
      this.workspaceService.getUserWorkspaces().subscribe({
        next: (workspaces) => {
          this.workspaces = workspaces;
          this.sortWorkspaces();
          this.selectedWorkspaceId = this.workspaceService.getCurrentWorkspaceFromStorage();
          if (!this.selectedWorkspaceId && workspaces.length > 0) {
            this.subscription.add(
              this.workspaceService.getCurrentWorkspace().subscribe({
                next: (ws) => {
                  if (ws) this.selectedWorkspaceId = ws.id;
                },
              })
            );
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      })
    );
  }

  private sortWorkspaces(): void {
    if (!this.user || !this.workspaces.length) return;

    // Pin personal workspace (owner_id == user.id) at top
    this.workspaces.sort((a, b) => {
      const aIsPersonal = a.owner_id === this.user!.id;
      const bIsPersonal = b.owner_id === this.user!.id;
      if (aIsPersonal && !bIsPersonal) return -1;
      if (!aIsPersonal && bIsPersonal) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  isPersonalWorkspace(workspace: Workspace): boolean {
    return !!this.user && workspace.owner_id === this.user.id;
  }

  getInitial(name: string): string {
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  onWorkspaceChange(workspaceId: string): void {
    const currentId = this.workspaceService.getCurrentWorkspaceFromStorage();
    if (workspaceId === currentId) return;

    this.workspaceService.switchWorkspaceAndNavigate(workspaceId);
  }

  get selectedWorkspace(): Workspace | undefined {
    return this.workspaces.find((w) => w.id === this.selectedWorkspaceId);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
